import React, { useEffect, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { CompanyTag } from '@/components/ui/badge'
import { State } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { TradingAccountDto } from 'api/Clients/Types/TradingAccount'
import { PageData } from 'api/Pagination/Types'
import { Pagination } from 'api/Pagination/Types/Pagination'
import { IntroducingBrokerDetailsDto } from 'api/Trading-Accounts/Types/IntroducingBrokerDetailsDto'
import { Link } from 'components/Link'
import { Spinner } from 'components/Spinner'
import omitEmpty from 'omit-empty'
import { pageableSettings } from 'store/grid/grid.slice'
import { useApiClient } from 'utils/api/ApiClient'
import { PartnershipsApiClient } from 'utils/api/partnershipsApi'
import { links } from 'utils/featureToggles'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { TableWithExport } from 'utils/table/withExport'

import {
  IntroducingBrokerNavigation,
  IntroducingBrokerTab,
} from './Navigation/IntroducingBrokerNavigation'

const createDataState = (state: State, data: TradingAccountDto[], total: number) => ({
  result: data,
  dataState: state,
  total,
})

interface Props {
  data: IntroducingBrokerDetailsDto
}

const IntroducingBrokerTradingAccounts: React.FC<Props> = ({ data }) => {
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)

  const [loading, setLoading] = useState<boolean>(true)
  const [paginationData, setPaginationData] = useState<PageData<TradingAccountDto[]> | null>(null)

  const [dataState, setDataState] = useState<{
    result: TradingAccountDto[]
    dataState: State
    total: number
  }>(
    createDataState(
      getPaginationInitialState(),
      paginationData?.items ?? [],
      paginationData?.totalResults ?? 0
    )
  )

  useEffect(() => {
    ;(async () => {
      try {
        setLoading(true)

        const tradingAccountsResponse =
          await partnershipsApiClient.getTradingAccountsByIntroducingBrokerId(data.id)

        setDataState(
          createDataState(
            {
              take: dataState.dataState.take,
              skip: dataState.dataState.skip,
            },
            tradingAccountsResponse.items,
            tradingAccountsResponse.totalResults
          )
        )
        setLoading(false)
      } catch (err) {
        console.error(err)
        setLoading(false)
      }
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const pageChange = async (event: GridPageChangeEvent) => {
    const { skip, take } = event.page
    const pagination = new Pagination({
      skip: event.page.skip,
      recordsPerPage: take,
    })

    const sessionPageSize = sessionStorage.getItem('itemsPerPage')
    if (event.page.take.toString() !== sessionPageSize)
      sessionStorage.setItem('itemsPerPage', event.page.take.toString())

    const query = omitEmpty({
      pageIndex: pagination.getPageIndex() || 1,
      pageSize: take,
    })
    const tradingAccountsResponse =
      await partnershipsApiClient.getTradingAccountsByIntroducingBrokerId(data.id, query)

    setDataState(
      createDataState(
        {
          take,
          skip,
        },
        tradingAccountsResponse.items || [],
        tradingAccountsResponse.totalResults
      )
    )
    setPaginationData(tradingAccountsResponse)
  }

  if (loading) {
    return <Spinner />
  }

  return (
    <React.Fragment>
      <IntroducingBrokerNavigation
        data={data}
        isEdit={false}
        tab={IntroducingBrokerTab.TRADING_ACCOUNTS}
      />
      <TableWithExport data={dataState.result || []}>
        <Grid
          resizable
          take={dataState.dataState.take}
          skip={dataState.dataState.skip}
          onDataStateChange={() => {}}
          total={dataState.total}
          data={dataState.result}
          onPageChange={pageChange}
          sortable={{
            allowUnsort: true,
            mode: 'multiple',
          }}
          pageable={pageableSettings}
        >
          <Column
            title='Name'
            field='name'
            cell={(gridCellProps: GridCellProps) => (
              <td
                className='table-columns__content'
                style={{
                  color: 'var(--link)',
                  textDecoration: 'underline',
                }}
              >
                <Link
                  to={{
                    pathname: generatePath(links.recordsTradingAccounts.detailsUrl as string, {
                      id: gridCellProps.dataItem.id,
                      slug: 'details',
                    }),
                  }}
                >
                  {gridCellProps.dataItem.name}
                </Link>
              </td>
            )}
            filter='text'
          />
          <Column
            title='Wallet'
            cell={(gridCellProps: GridCellProps) => (
              <td
                className='table-columns__content'
                style={{
                  color: 'var(--link)',
                  textDecoration: 'underline',
                }}
              >
                <Link
                  to={{
                    pathname: generatePath(links.recordsWallets.detailsUrl as string, {
                      id: gridCellProps.dataItem.wallet.id,
                      slug: 'details',
                    }),
                  }}
                >
                  {gridCellProps.dataItem.wallet.name}
                </Link>
              </td>
            )}
            filter='text'
          />
          <Column
            title='Account Group'
            cell={(gridCellProps: GridCellProps) => (
              <td
                className='table-columns__content'
                style={{
                  color: 'var(--link)',
                  textDecoration: 'underline',
                }}
              >
                <Link
                  to={{
                    pathname: generatePath(links.recordsAccountGroups.detailsUrl as string, {
                      id: gridCellProps.dataItem.accountGroup.id,
                      slug: 'details',
                    }),
                  }}
                >
                  {gridCellProps.dataItem.accountGroup.name}
                </Link>
              </td>
            )}
            field='accountGroupType.name'
            filter='text'
          />
          <Column title='Account Type' field='accountType.name' filter='text' />
          <Column title='Currency' field='currency.id' filter='text' />
          <Column title='Status' field='status.name' filter='text' />
          <Column title='Country' field='country.name' filter='text' />
          <Column
            title='Entity'
            cell={(gridCellProps: GridCellProps) => {
              if (!gridCellProps.dataItem?.tickmillCompany?.id) {
                return <td />
              }

              return (
                <td>
                  <CompanyTag id={gridCellProps.dataItem.tickmillCompany.id}>
                    {gridCellProps.dataItem.tickmillCompany.name}
                  </CompanyTag>
                </td>
              )
            }}
            filter='text'
            width={80}
            minResizableWidth={80}
          />
        </Grid>
      </TableWithExport>
    </React.Fragment>
  )
}

export default IntroducingBrokerTradingAccounts
